import React, { PureComponent } from 'react';
import { string, number, arrayOf, shape, oneOfType } from 'prop-types';
import { VictoryPie, VictoryLabel, VictoryAxis } from 'victory';
import { toCommaNumber, shortenFormat } from 'common/formatters';
import { browserInfo, isDesktop } from 'common/utils';
import { COLOR_SCALE } from 'common/constants';
import IEVictoryChart from 'common/IEVictoryChart';
import { ChartLegend } from 'common/components';
import CustomDataComponent from './CustomDataComponent';

import './index.css';

/**
 *
 *
 * @export
 * @class DoughnutChart
 * @extends {PureComponent}
 */
export class DoughnutChart extends PureComponent {
  static displayName = 'DoughnutChart';

  static propTypes = {
    width: number,
    colorScale: arrayOf(string),
    data: arrayOf(
      shape({
        x: string,
        y: oneOfType([string, number]),
        unit: string,
      })
    ),
    innerRadius: number,
  };

  static defaultProps = {
    width: 300,
    colorScale: COLOR_SCALE,
    data: [],
    innerRadius: isDesktop() ? 112 : 90,
  };

  state = initialState(this.props.data);

  render() {
    const { data, width, colorScale, innerRadius } = this.props;
    const { pieSectionName, pieSectionValue, pieSectionUnit } = this.state;
    const fontRoman = { fontFamily: '"Univers LT Roman", sans-serif' };
    const fontBold = {
      fontFamily: '"Univers LT Bold", sans-serif',
      fontWeight: 'bold', // fontWeight set in case "Univers LT Bold" font not loaded
    };

    const centerLabel = this._getCenterLabel(pieSectionName);
    let dataText = toCommaNumber(pieSectionValue);
    dataText =
      pieSectionUnit === '$' ? [`$${dataText}`] : [dataText, pieSectionUnit];
    const legendData = this._getLegendData(data);

    const chart = (
      <div className="doughnutchart">
        <IEVictoryChart height={width} width={width}>
          <VictoryPie
            name="pie"
            standalone={false}
            colorScale={colorScale}
            data={data}
            dataComponent={
              <CustomDataComponent pieSectionName={pieSectionName} />
            }
            innerRadius={innerRadius}
            cornerRadius={0}
            style={{ labels: { fontSize: 0, fill: 'transparent' } }}
            width={width}
            height={width}
            events={this._getEventHandlers(data)}
          />
          <VictoryLabel
            className="doughnut-label"
            name="label"
            textAnchor="middle"
            style={[
              { ...fontBold, fontSize: 20 },
              { ...fontRoman, fontSize: 20 },
            ]}
            x={width / 2}
            y={width / 2 - 25}
            text={centerLabel}
          />
          <VictoryLabel
            className="doughnut-label"
            name="label"
            textAnchor="middle"
            style={[
              { ...fontBold, fontSize: 25 },
              { ...fontRoman, fontSize: 18 },
            ]}
            x={width / 2}
            y={width / 2 + 30}
            text={dataText}
          />
          <VictoryAxis
            tickFormat={() => ''}
            style={{ axis: { stroke: 'none' } }}
          />
        </IEVictoryChart>
        <ChartLegend
          data={legendData}
          colorScale={colorScale}
          theme={['2col', 'narrow']}
        />
      </div>
    );
    return chart;
  }

  /**
   *
   *
   * @memberof DoughnutChart
   * @param {String} - name ex. Beans Beaniest
   * @returns {Array} - ['Beans', 'Beaniest']
   */
  _getCenterLabel = str => {
    const [title, variety] = str.split('\n', 2);
    return [shortenFormat(title, 12), shortenFormat(variety, 16)];
  };

  /**
   *
   *
   * @memberof DoughnutChart
   * @param {Object} - data from graphql
   * Get crop names for the legend
   */
  _getLegendData = data => data.map(({ x }) => x.split(' ')[0]);

  /**
   *
   *
   * @memberof DoughnutChart
   * @param {Object} - data from graphql
   * @returns {Array}
   */
  _getEventHandlers = () => {
    const iOS = browserInfo().parsedResult.os.name === 'iOS';
    let events = [
      {
        target: 'data',
        eventHandlers: {
          onMouseOver: () => this._getEventBehaviour(),
        },
      },
    ];

    if (iOS) {
      events = [
        {
          target: 'data',
          eventHandlers: {
            onClick: () => this._getEventBehaviour(),
          },
        },
      ];
    }

    return events;
  };

  /**
   *
   *
   * @memberof DoughnutChart
   * @returns {Array}
   */
  _getEventBehaviour = () => {
    return [
      {
        mutation: props => {
          this.setState({
            pieSectionName: props.datum ? props.datum.x : '',
            pieSectionValue: props.datum ? props.datum.y : '',
          });
        },
      },
    ];
  };
}

export default DoughnutChart;

/**
 *
 * @function initialState
 * @param {Object} - data from graphql
 * @returns {Object}
 */
export const initialState = data => {
  // We want to show the crop with the largest acreage as default state
  const largestCrop = data.reduce(
    (prev, cur) => (parseInt(prev.y, 10) > parseInt(cur.y, 10) ? prev : cur),
    {}
  );

  return {
    pieSectionName: largestCrop.x,
    pieSectionValue: largestCrop.y,
    pieSectionUnit: largestCrop.unit,
  };
};



// WEBPACK FOOTER //
// ./src/common/components/DoughnutChart/index.js